import { Observable, BehaviorSubject, throwError } from "rxjs";
import { switchMap, catchError, finalize, shareReplay } from "rxjs/operators";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UsuarioPreferenciasService } from "../services/data-services/usuario.preferencias.service";
import { AuthService } from "../modules/shared/services/auth.service";
import { ErrorHandlerService } from "../services/errorHandler.service";
import { StatusService } from "../services/status.service";
import { MENSAGEM_SESSAO_EXPIRADA } from "../modules/shared/resources/messages";

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshToken = "";
  private refreshToken$ = new BehaviorSubject<any>(this.refreshToken);
  private chamadaCompartilhada$: Observable<any> | null = null;

  constructor(
    private auth: AuthService,
    private userDataService: UsuarioPreferenciasService,
    private errorHandlerService: ErrorHandlerService,
    private statusService: StatusService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      request.url.indexOf("api.supervademecum.com.br") === -1 &&
      request.url.indexOf("localhost") === -1
    ) {
      return next.handle(request);
    }

    return next.handle(this.addAuthenticationToken(request)).pipe(
      catchError((error) => {
        // Qualquer erro que não seja 401 (403, 500, rede, etc.) precisa
        // continuar fluindo no stream. O mesmo vale para o 401 do próprio
        // login/refresh: tentar renovar o token a partir deles faria a
        // renovação esperar por ela mesma e travar a tela.
        if (error.status !== 401 || this.isRequisicaoAutenticacao(request)) {
          return throwError(() => error);
        }

        if (!this.chamadaCompartilhada$) {
          const data = this.userDataService.getConfiguracoesvalue;

          if (!data?.email || !data?.authToken) {
            // Sem dados para renovar o token (ex.: sessão nunca sincronizou
            // ou já foi limpa) > desloga direto em vez de travar a tela
            this.auth.logoff();
            this.statusService.setMessage(MENSAGEM_SESSAO_EXPIRADA);
            return throwError(() => error);
          }

          this.chamadaCompartilhada$ = this.auth
            .refreshToken(data.email, data.authToken)
            .pipe(
              catchError((errorRefresh: any) => {
                this.auth.logoff();
                this.statusService.setMessage(MENSAGEM_SESSAO_EXPIRADA);
                this.errorHandlerService.handleError(errorRefresh);
                return throwError(() => errorRefresh);
              }),
              finalize(() => {
                // Limpa o estado após a renovação terminar
                this.chamadaCompartilhada$ = null;
              }),
              shareReplay(1) // uma unica renovacao para todas as chamadas
            );
        }

        // Só a renovação é compartilhada: cada chamada refaz a sua própria
        // requisição com o token novo
        return this.chamadaCompartilhada$.pipe(
          switchMap((token: any) => {
            this.refreshToken = token;
            this.updateRefreshToken();
            return next.handle(this.addAuthenticationToken(request));
          })
        );
      })
    );
  }

  addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.auth.getAccessToken();

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: { Authorization: "bearer " + token.token },
    });
  }

  updateRefreshToken() {
    this.refreshToken$.next(this.refreshToken);
  }

  getRefreshToken(): Observable<boolean> {
    return this.refreshToken$.asObservable();
  }

  private isRequisicaoAutenticacao(request: HttpRequest<any>): boolean {
    return (
      request.url.indexOf("/auth/login") !== -1 ||
      request.url.indexOf("/auth/refresh-token") !== -1
    );
  }
}
